import { lazy } from "react";
import * as XLSX from "xlsx";

export const sections = [
  {
    id: "beneficiaryData",
    title: "Dados do Beneficiário",
    component: lazy(() => import("./01_Beneficiary")),
  },
  {
    id: "economic",
    title: "Unidade Económica",
    component: lazy(() => import("./02_EconomicUnit")),
  },
  {
    id: "marketAnalysis",
    title: "Análise Mercado",
    component: lazy(() => import("./03_MarketAnalysis")),
  },
  {
    id: "balance",
    title: "Balanço e DR",
    component: lazy(() => import("./04_BalanceDR")),
  },
  {
    id: "project",
    title: "Dados do Projeto",
    component: lazy(() => import("./05_ProjectData")),
  },
  {
    id: "typologies",
    title: "Tipologias",
    component: lazy(() => import("./06_Typologies")),
  },
  {
    id: "activities",
    title: "Lista de Atividades e indicadores",
    component: lazy(() => import("./07_Activities")),
  },
  {
    id: "costs",
    title: "Custos",
    component: lazy(() => import("./08_Costs")),
  },
  {
    id: "operationDescription",
    title: "Descrição da Operação",
    component: lazy(() => import("./09_OperationDescription")),
  },
  {
    id: "climateTransaction",
    title: "Transição Climática",
    component: lazy(() => import("./10_ClimateTransition")),
  },
  {
    id: "financing",
    title: "Financiamento",
    component: lazy(() => import("./11_Financing")),
  },
  {
    id: "thematic",
    title: "Enquadramento Temático",
    component: lazy(() => import("./12_Thematic")),
  },
  {
    id: "selectionCriteria",
    title: "Critérios de Seleção",
    component: lazy(() => import("./13_SelectionCriteria")),
  },
  {
    id: "declarations",
    title: "Declarações",
    component: lazy(() => import("./14_Declarations")),
  },
];

export const importFiles = {
  Mérito: (workbook, formData, jsonData, handleChange, setToast) => {
    const balanceSheet = workbook.Sheets[`Balanço`];
    const balaceData = XLSX.utils.sheet_to_json(balanceSheet, {
      range: "C9:L71",
      blankrows: true,
      defval: "",
      skipHidden: false,
      header: ["23", "24", "25", "26", "27", "28", "29", "30", "31", "32"],
    });

    const drSheet = workbook.Sheets[`DR`];
    const drData = XLSX.utils.sheet_to_json(drSheet, {
      range: "C11:L40",
      blankrows: true,
      defval: "",
      skipHidden: false,
      header: ["23", "24", "25", "26", "27", "28", "29", "30", "31", "32"],
    });

    const financeSheet = workbook.Sheets[`Financiamento Inov`];
    const financingData = XLSX.utils.sheet_to_json(financeSheet, {
      range: "K4:K20",
      blankrows: true,
      defval: "",
      skipHidden: false,
      header: ["value"],
    });

    let id = 1;
    const costsSheet = workbook.Sheets["Quadro de Investimentos Inov"];
    const costsData = XLSX.utils
      .sheet_to_json(costsSheet, {
        range: "B5:J79",
        blankrows: true,
        defval: "",
        skipHidden: false,
        header: [
          "designation",
          "act",
          "loc",
          "u1",
          "u2",
          "acquisitionDate",
          "u4",
          "category",
          "eligibleCost",
        ],
      })
      .map((cost) => {
        if (!cost.designation || cost.elegibleCost <= 0) return;
        const activityLabel = splitOnce(cost.act, "-");
        const locationLabel = splitOnce(cost.loc, "-");

        const category =
          jsonData["CostsCategory"].find(
            (cat) => cat.label === cost.category?.trim(),
          )?.value ?? "";
        const activity = String(
          formData["projectActivities"]?.find(
            (act) => act.designation === activityLabel,
          )?.id ?? "",
        );

        const location =
          formData["projectLocation"]?.length === 1
            ? String(formData["projectLocation"][0].id)
            : String(
                formData["projectLocation"]?.find(
                  (loc) => loc.address === locationLabel,
                )?.id ?? "",
              );

        const acquisitionDate =
          excelSerialToJSDate(cost.acquisitionDate)
            ?.toISOString()
            .slice(0, 7) ?? "";

        return {
          id: id++,
          designation: cost.designation,
          category,
          activity,
          institute: location,
          acquisitionDate,
          eligibleCost: cost.eligibleCost,
        };
      })
      .filter(Boolean);

    const meritoSheet = workbook.Sheets["Mérito Inov"];
    if (!meritoSheet) {
      setToast({
        title: "Mérito",
        message: 'Não foi encontrada a folha "Mérito Inov" no ficheiro.',
        type: "error",
      });
      return;
    }

    const activityPerMarketData = XLSX.utils
      .sheet_to_json(meritoSheet, {
        range: "L37:R81",
        blankrows: true,
        defval: "",
        skipHidden: false,
        header: [
          "service",
          "new",
          "country",
          "preQuantity",
          "preValue",
          "postQuantity",
          "postValue",
        ],
      })
      .map((act) => {
        if (!act.country) return;

        const service = jsonData["Services"]?.find(
          (service) => service.label === act.service,
        )?.value;
        const country = jsonData["Countries"]?.find(
          (country) => country.label === act.country,
        )?.value;
        const new_ = act.new === "Existente" ? "E" : "N";

        return { ...act, type: "01", service, new: new_, country };
      })
      .filter(Boolean);

    const indirectExportationData = XLSX.utils
      .sheet_to_json(meritoSheet, {
        range: "U29:AC41",
        blankrows: true,
        defval: "",
        skipHidden: false,
        header: [
          "nif",
          "label",
          "preSells",
          "preVolInter",
          "preVolTotal",
          "u1",
          "postSells",
          "postVolInter",
          "postVolTotal",
        ],
      })
      .map((exp) => {
        if (!exp.label) return;

        delete exp.u1;

        return exp;
      })
      .filter(Boolean);

    const subImportsData = XLSX.utils
      .sheet_to_json(meritoSheet, {
        range: "AE29:AL41",
        blankrows: true,
        defval: "",
        skipHidden: false,
        header: [
          "nif",
          "label",
          "u1",
          "preImports",
          "preBuys",
          "postImports",
          "postSells",
          "postBuys",
          "subsImports",
        ],
      })
      .map((exp) => {
        if (!exp.label) return;

        delete exp.u1;

        return exp;
      })
      .filter(Boolean);

    const conditionsSheet = workbook.Sheets["Condições Validação Inov"];

    const startDate =
      excelSerialToJSDate(conditionsSheet?.["D9"]?.v)
        ?.toISOString()
        .slice(0, 10) ?? "";
    const endDate =
      excelSerialToJSDate(conditionsSheet?.["L9"]?.v)
        ?.toISOString()
        .slice(0, 10) ?? "";
    const preProjectYear = conditionsSheet?.["H7"]?.v
      ? String(conditionsSheet["H7"].v)
      : "";
    const cruiseYear = conditionsSheet?.["L7"]?.v
      ? String(conditionsSheet["L7"].v)
      : "";

    const projectCAES = conditionsSheet
      ? XLSX.utils
          .sheet_to_json(conditionsSheet, {
            range: "C53:D55",
            blankrows: true,
            defval: "",
            skipHidden: false,
            header: ["%", "cae"],
          })
          .map((cae) => {
            if (!cae.cae) return;
            return cae;
          })
          .filter(Boolean)
      : [];

    const jobsPre = conditionsSheet?.["F26"]?.v ?? "";
    const jobsPost = conditionsSheet?.["H26"]?.v ?? "";
    const qualifiedJobsPre = conditionsSheet?.["F27"]?.v ?? "";
    const qualifiedJobsPost = conditionsSheet?.["H27"]?.v ?? "";

    const jobsCreation = [
      {
        rubric: "Pré-Projeto",
        jobsTotal: jobsPre,
        jobsQual: qualifiedJobsPre,
        etiTotal: jobsPre,
        etiQual: qualifiedJobsPre,
      },
      {
        rubric: "Ano Conclusão + 1",
        jobsTotal: jobsPost,
        jobsQual: qualifiedJobsPost,
        etiTotal: jobsPost,
        etiQual: qualifiedJobsPost,
      },
      {
        rubric: "Criação Líquida de Emprego",
      },
      {
        rubric: "Ano Cruzeiro",
        jobsTotal: jobsPost,
        jobsQual: qualifiedJobsPost,
        etiTotal: jobsPost,
        etiQual: qualifiedJobsPost,
      },
      {
        rubric: "Criação Líquida de Emprego",
      },
    ];

    const economicActivitiesCapacityIncrease = XLSX.utils
      .sheet_to_json(meritoSheet, {
        range: "L89:S93",
        blankrows: true,
        defval: "",
        skipHidden: false,
        header: [
          "service",
          "existance",
          "preProductionCapacityUnit",
          "preProductionCapacityAmount",
          "preGrossProductionValue",
          "postProductionCapacityUnit",
          "postProductionCapacityAmount",
          "postGrossProductionValue",
        ],
      })
      .map((act) => {
        if (!act.service || act.service === "(escolha uma opção)") return;

        const service = jsonData["Services"]?.find(
          (service) => service.label === act.service,
        )?.value;
        const new_ = act.new === "Existente" ? "E" : "N";

        return { ...act, service, new: new_ };
      })
      .filter(Boolean);

    const projectIndicators =
      jsonData["Indicators"] ||
      [
        "RPO008",
        "RCR01",
        "RPR003",
        "RSR23",
        "RPR001",
        "RPR031",
        "RPR002",
        "RPR080",
        "RPA001",
        "RPA002",
        "RPA003",
        "RPA004",
      ].map((code) => {
        return { code };
      });

    const setIndicatorGoal = (code, value) => {
      const indicator = projectIndicators.find((ind) => ind.code === code);
      if (!indicator) return;
      indicator.goal = value;
    };

    setIndicatorGoal("RPO008", conditionsSheet?.["H28"]?.v);

    setIndicatorGoal("RCR01", conditionsSheet?.["J26"]?.v);
    setIndicatorGoal("RPR003", conditionsSheet?.["J26"]?.v);

    setIndicatorGoal("RSR23", conditionsSheet?.["J27"]?.v);

    setIndicatorGoal("RPR001", conditionsSheet?.["J29"]?.v);
    setIndicatorGoal("RPR031", conditionsSheet?.["J30"]?.v);

    setIndicatorGoal("RPR002", conditionsSheet?.["J31"]?.v);
    setIndicatorGoal("RPR080", conditionsSheet?.["J32"]?.v);

    // Mérito keeps these percentages as fractions (0.25 -> 25); an empty cell
    // stays empty (undefined * 100 would be NaN).
    const percentGoal = (cell) =>
      typeof conditionsSheet?.[cell]?.v === "number"
        ? Math.round(conditionsSheet[cell].v * 10000) / 100
        : "";
    setIndicatorGoal("RPA001", percentGoal("J36"));
    setIndicatorGoal("RPA002", percentGoal("J37"));
    setIndicatorGoal("RPA003", percentGoal("J38"));

    handleChange("balance", balaceData);
    handleChange("dr", drData);

    handleChange("financing", financingData);
    handleChange("projectCosts", costsData);

    handleChange("activityPerMarket", activityPerMarketData);
    handleChange("indirectExportation", indirectExportationData);
    handleChange("subImports", subImportsData);

    if (startDate) handleChange("projectStartDate", startDate);
    if (endDate) handleChange("projectEndDate", endDate);
    if (preProjectYear) handleChange("projectPreYear", preProjectYear);
    if (cruiseYear) handleChange("projectCruiseYear", cruiseYear);
    if (projectCAES.length) handleChange("projectCAE", projectCAES);
    handleChange("jobsCreation", jobsCreation);
    handleChange("projectIndicators", projectIndicators);

    const capacityIncreaseTypology = (formData["projectTypologies"] ?? []).find(
      (typology) => typology.typology === "22",
    );
    if (capacityIncreaseTypology && economicActivitiesCapacityIncrease.length) {
      const updatedTypologies = formData["projectTypologies"].map((typology) =>
        typology.id === capacityIncreaseTypology.id
          ? {
              ...typology,
              economicActivity: economicActivitiesCapacityIncrease,
            }
          : typology,
      );
      handleChange("projectTypologies", updatedTypologies);
    }

    setToast({
      title: "Mérito",
      message: "Dados foram carregados com sucesso!",
      type: "success",
    });
  },
};

function splitOnce(str, delimiter) {
  if (!str) return "";

  const index = str.indexOf(delimiter);
  if (index === -1) return "";
  return str.slice(index + delimiter.length).trim();
}

function excelSerialToJSDate(xlSerial) {
  const out = new Date(
    -2209075200000 + (xlSerial - (xlSerial < 61 ? 0 : 1)) * 86400000,
  );
  if (isNaN(out.getTime())) {
    return null; // Invalid date
  }
  return out;
}
